import React, { useState } from 'react';
import { FaRupeeSign, FaDownload, FaRedo, FaLayerGroup, FaPlus, FaTrash } from 'react-icons/fa';
import { formatCurrency } from '../utils/calculations';
import { generateGenericPDF } from '../utils/pdfGenerator';
import SEO from '../components/SEO';

const StockAverageCalculator = () => {
  const [purchases, setPurchases] = useState([
    { id: 1, price: 100, quantity: 50 },
    { id: 2, price: 90, quantity: 100 },
  ]);
  const [targetPrice, setTargetPrice] = useState(120);

  const addPurchase = () => {
    const newId = purchases.length > 0 ? Math.max(...purchases.map(p => p.id)) + 1 : 1;
    setPurchases([...purchases, { id: newId, price: 0, quantity: 0 }]);
  };

  const removePurchase = (id) => {
    if (purchases.length > 1) {
      setPurchases(purchases.filter(p => p.id !== id));
    }
  };

  const updatePurchase = (id, field, value) => {
    setPurchases(purchases.map(p => 
      p.id === id ? { ...p, [field]: Number(value) } : p
    ));
  };

  const calculateResults = () => {
    const validPurchases = purchases.filter(p => p.price > 0 && p.quantity > 0);
    
    if (validPurchases.length === 0) return null;
    
    const totalQuantity = validPurchases.reduce((sum, p) => sum + p.quantity, 0);
    const totalInvestment = validPurchases.reduce((sum, p) => sum + (p.price * p.quantity), 0);
    const averagePrice = totalInvestment / totalQuantity;
    
    const currentValue = targetPrice * totalQuantity;
    const profitLoss = currentValue - totalInvestment;
    const profitLossPercent = (profitLoss / totalInvestment) * 100;
    
    // Calculate break-even price
    const breakEvenPrice = averagePrice;
    
    // Calculate what quantity to buy at current price to achieve target average
    const targetAverages = [
      { targetAvg: averagePrice * 0.95, label: '5% lower avg' },
      { targetAvg: averagePrice * 0.90, label: '10% lower avg' },
      { targetAvg: averagePrice * 0.85, label: '15% lower avg' },
    ];
    
    const avgDownOptions = targetAverages.map(({ targetAvg, label }) => {
      // newAvg = (totalInvestment + newPrice * newQty) / (totalQty + newQty)
      // Solving for newQty at a lower price point:
      const newPrice = averagePrice * 0.8; // Assuming buying 20% lower
      const requiredQty = (totalInvestment - targetAvg * totalQuantity) / (targetAvg - newPrice);
      return {
        label,
        targetAvg: targetAvg.toFixed(2),
        buyPrice: newPrice.toFixed(2),
        requiredQty: Math.max(0, Math.ceil(requiredQty)),
        additionalInvestment: Math.max(0, Math.ceil(requiredQty) * newPrice),
      };
    });
    
    return {
      totalQuantity,
      totalInvestment,
      averagePrice,
      currentValue,
      profitLoss,
      profitLossPercent,
      breakEvenPrice,
      avgDownOptions,
      validPurchases,
    };
  };

  const result = calculateResults();

  const handleDownloadPDF = () => {
    if (!result) return;
    generateGenericPDF('Stock Average Calculator Report', {
      'Total Shares': result.totalQuantity,
      'Total Investment': formatCurrency(result.totalInvestment),
      'Average Price': formatCurrency(result.averagePrice),
      'Target/Current Price': formatCurrency(targetPrice),
      'Current Value': formatCurrency(result.currentValue),
      'Profit/Loss': formatCurrency(result.profitLoss),
      'Return %': `${result.profitLossPercent.toFixed(2)}%`,
      'Break-even Price': formatCurrency(result.breakEvenPrice),
    });
  };

  const handleReset = () => {
    setPurchases([
      { id: 1, price: 100, quantity: 50 },
      { id: 2, price: 90, quantity: 100 },
    ]);
    setTargetPrice(120);
  };

  return (
    <div className="calculator-page fade-in">
      <SEO 
        title="Stock Average Calculator"
        description="Free Stock Average Calculator - Calculate average price when buying stocks at different prices."
        keywords="stock average calculator, share average calculator, average cost calculator, stock averaging calculator, cost averaging calculator"
        canonicalPath="/stock-average"
      />
      <div className="calculator-header">
        <h1><FaLayerGroup style={{ color: '#6366f1' }} /> Stock Average Calculator</h1>
        <p>Calculate weighted average price for multiple stock purchases</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              Your Purchases
              <button 
                onClick={addPurchase}
                style={{
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '4px 12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '12px',
                }}
              >
                <FaPlus /> Add Purchase
              </button>
            </label>
            
            <div style={{ marginTop: '12px' }}>
              {purchases.map((purchase, index) => (
                <div 
                  key={purchase.id} 
                  style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr auto', 
                    gap: '8px', 
                    marginBottom: '8px',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <small style={{ color: 'var(--text-secondary)', fontSize: '10px' }}>Price (₹)</small>
                    <input
                      type="number"
                      value={purchase.price}
                      onChange={(e) => updatePurchase(purchase.id, 'price', e.target.value)}
                      placeholder="Buy Price"
                      min="0.01"
                      step="0.01"
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div>
                    <small style={{ color: 'var(--text-secondary)', fontSize: '10px' }}>Quantity</small>
                    <input
                      type="number"
                      value={purchase.quantity}
                      onChange={(e) => updatePurchase(purchase.id, 'quantity', e.target.value)}
                      placeholder="Qty"
                      min="1"
                      style={{ width: '100%' }}
                    />
                  </div>
                  <button
                    onClick={() => removePurchase(purchase.id)}
                    disabled={purchases.length <= 1}
                    style={{
                      background: purchases.length <= 1 ? '#e5e7eb' : '#fee2e2',
                      color: purchases.length <= 1 ? '#9ca3af' : '#dc2626',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '8px',
                      cursor: purchases.length <= 1 ? 'not-allowed' : 'pointer',
                      marginTop: '16px',
                    }}
                  >
                    <FaTrash />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="input-group">
            <label>Target / Current Market Price (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={targetPrice === '' ? '' : targetPrice}
                onChange={(e) => setTargetPrice(e.target.value === '' ? '' : Number(e.target.value))}
                min="0.01"
                step="0.01"
              />
            </div>
          </div>

          <div className="button-group">
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: '8px' }} /> Download PDF
            </button>
          </div>
        </div>

        <div className="calculator-results">
          {result ? (
            <>
              <div className="result-summary" style={{ background: 'linear-gradient(135deg, #6366f1, #4f46e5)' }}>
                <h3>Average Buy Price</h3>
                <div className="amount">{formatCurrency(result.averagePrice)}</div>
                <small>Weighted average of {result.totalQuantity} shares</small>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                <div className="result-item" style={{ background: '#eff6ff', textAlign: 'center', padding: '16px' }}>
                  <span className="label">Total Investment</span>
                  <span className="value" style={{ color: '#2563eb' }}>{formatCurrency(result.totalInvestment)}</span>
                </div>
                <div className="result-item" style={{ 
                  background: result.profitLoss >= 0 ? '#f0fdf4' : '#fef2f2', 
                  textAlign: 'center', 
                  padding: '16px' 
                }}>
                  <span className="label">Profit/Loss</span>
                  <span className="value" style={{ color: result.profitLoss >= 0 ? '#16a34a' : '#dc2626' }}>
                    {formatCurrency(result.profitLoss)} ({result.profitLossPercent.toFixed(2)}%)
                  </span>
                </div>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Total Shares</span>
                  <span className="value">{result.totalQuantity}</span>
                </div>
                <div className="result-item">
                  <span className="label">Current Value @ ₹{targetPrice}</span>
                  <span className="value">{formatCurrency(result.currentValue)}</span>
                </div>
                <div className="result-item" style={{ background: '#fef3c7' }}>
                  <span className="label">Break-even Price</span>
                  <span className="value" style={{ fontWeight: 'bold' }}>{formatCurrency(result.breakEvenPrice)}</span>
                </div>
              </div>

              {/* Purchase Breakdown */}
              <div className="breakup-section">
                <h4>Purchase Breakdown</h4>
                <table className="tax-slab-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Price</th>
                      <th>Qty</th>
                      <th>Value</th>
                      <th>Weight</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.validPurchases.map((p, index) => (
                      <tr key={p.id}>
                        <td>{index + 1}</td>
                        <td>{formatCurrency(p.price)}</td>
                        <td>{p.quantity}</td>
                        <td>{formatCurrency(p.price * p.quantity)}</td>
                        <td>{((p.quantity / result.totalQuantity) * 100).toFixed(1)}%</td>
                      </tr>
                    ))}
                    <tr style={{ background: '#dbeafe', fontWeight: 'bold' }}>
                      <td colSpan="2">Total</td>
                      <td>{result.totalQuantity}</td>
                      <td>{formatCurrency(result.totalInvestment)}</td>
                      <td>100%</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Average Down Scenarios */}
              {result.averagePrice > targetPrice && (
                <div className="breakup-section" style={{ marginTop: '20px' }}>
                  <h4>Averaging Down Scenarios</h4>
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '10px' }}>
                    If you buy more at 20% below current average:
                  </p>
                  <table className="tax-slab-table">
                    <thead>
                      <tr>
                        <th>Target</th>
                        <th>New Avg</th>
                        <th>Buy at</th>
                        <th>Qty Needed</th>
                        <th>Investment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.avgDownOptions.map((opt, index) => (
                        <tr key={index}>
                          <td>{opt.label}</td>
                          <td>₹{opt.targetAvg}</td>
                          <td>₹{opt.buyPrice}</td>
                          <td>{opt.requiredQty}</td>
                          <td>{formatCurrency(opt.additionalInvestment)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>
              <p>Enter valid purchase details to calculate average</p>
            </div>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>📌 Stock Averaging Strategies</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '12px' }}>
          <div>
            <ul>
              <li><strong>Dollar Cost Averaging (DCA):</strong> Regular investments regardless of price</li>
              <li><strong>Averaging Down:</strong> Buying more when price falls to lower average</li>
            </ul>
          </div>
          <div>
            <ul>
              <li><strong>Averaging Up:</strong> Adding to winning positions</li>
              <li><strong>Formula:</strong> Avg = Total Investment / Total Shares</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockAverageCalculator;
